// 選択中オブジェクトに対して「どのスタイル変更パネルを出し、どのオブジェクトへ適用するか」を
// 解決する純粋関数。
//
// React にも zustand store にも実行時依存させないため、CanvasTool / CanvasObject は
// `import type` でのみ参照する。これにより全分岐を単体テストで検証できる。

import { SECTION_DEFAULT_FILL } from "@refinement-canvas/domain";
import type {
  CanvasObject,
  ConnectorCanvasObject,
  PenStrokeCanvasObject,
  SectionCanvasObject,
  ShapeCanvasObject,
  StrokePattern,
  StickyCanvasObject,
  TableCanvasObject,
  TextAlign,
  TextCanvasObject,
} from "@refinement-canvas/domain";
import type { CanvasTool } from "../store/canvas-store";
import {
  normalizeColorForCompare,
  resolvePenThicknessKind,
  type PenThickness,
} from "../../../lib/pen-preference";
import {
  normalizeShapeFillForCompare,
  normalizeShapeStrokeForCompare,
  resolveArrowStrokeColor,
  resolveShapeStrokeWidthKind,
  SHAPE_DEFAULT_STROKE,
  type ShapeStrokeWidth,
} from "../../../lib/shape-preference";
import {
  normalizeConnectorStrokeForCompare,
  resolveConnectorStrokeWidthKind,
} from "../../../lib/connector-style";
import { STICKY_DEFAULT_COLOR } from "../../../lib/sticky-preference";
import { DEFAULT_TEXT_FONT_SIZE } from "../../../lib/text-size-preference";
import { DEFAULT_TEXT_ALIGN } from "../../../lib/text-align-preference";
import { normalizeTextColorForCompare, normalizeTextFillForCompare } from "../../../lib/text-color";
import { resolveStrokePattern } from "../../../lib/stroke-pattern";

/** 選択編集パネルを提供するオブジェクト種別。 */
export type SelectionStyleKind =
  | "sticky"
  | "section"
  | "shape"
  | "connector"
  | "text"
  | "table"
  | "pen-stroke";

const SELECTION_STYLE_KINDS: readonly SelectionStyleKind[] = [
  "sticky",
  "section",
  "shape",
  "connector",
  "text",
  "table",
  "pen-stroke",
];

export interface SelectionStyleCommonValues {
  /** sticky / text / pen-stroke の色。混在なら null。 */
  readonly color: string | null;
  /** text / section / shape の背景色。背景なしは各種別の透明表現へ正規化する。混在なら null。 */
  readonly fill: string | null;
  /** text の文字サイズ（px）。混在なら null。 */
  readonly fontSize: number | null;
  /** text の太字。未設定は false、混在なら null。 */
  readonly bold: boolean | null;
  /** sticky / text の文字寄せ。混在なら null。 */
  readonly textAlign: TextAlign | null;
  /** pen-stroke の太さ（プリセット段階）。混在・プリセット外なら null。 */
  readonly thickness: PenThickness | null;
  /** shape / connector の線色。未設定は既定色、図形の線なし表現は transparent へ正規化する。 */
  readonly stroke: string | null;
  /** shape / connector の線幅（プリセット段階）。混在・プリセット外なら null。 */
  readonly strokeWidth: ShapeStrokeWidth | null;
  /** shape / pen-stroke の線種。欠落は実線へ正規化し、混在なら null。 */
  readonly strokePattern: StrokePattern | null;
}

export interface SelectionStyleTarget {
  readonly kind: SelectionStyleKind;
  /** スタイルを適用する対象 id（未ロックのみ）。 */
  readonly targetIds: readonly string[];
  readonly common: SelectionStyleCommonValues;
  /** shape の構成。図形以外では null。 */
  readonly shapeKindGroup: "fillable" | "arrow" | "mixed" | null;
}

const EMPTY_COMMON: SelectionStyleCommonValues = {
  color: null,
  fill: null,
  fontSize: null,
  bold: null,
  textAlign: null,
  thickness: null,
  stroke: null,
  strokeWidth: null,
  strokePattern: null,
};

/**
 * 選択中オブジェクトから、表示すべきパネルの種別・適用対象・現在の共通値を解決する。
 * パネルを出す条件を満たさない場合は null を返す。
 */
export function resolveSelectionStyleTarget(
  objects: Readonly<Record<string, CanvasObject>>,
  selectedObjectIds: readonly string[],
  tool: CanvasTool
): SelectionStyleTarget | null {
  // 1. 配置ツール・ペン描画中は既存のツール既定値パネルを優先し、パン中（panning）は既存の
  //    ツール別パネルと同じく何も表示しないため、idle 以外はすべて除外する。
  //    usesCrosshairCursor で判定すると、配置ツール中の中ボタンパンで panning へ移った瞬間だけ
  //    選択編集パネルが割り込むため採用しない。
  if (tool !== "idle") return null;

  // 2. 選択 id のうち、実在するオブジェクトだけを対象にする（他参加者が削除した直後など）。
  const selected: CanvasObject[] = [];
  for (const id of selectedObjectIds) {
    const object = objects[id];
    if (object) selected.push(object);
  }
  const first = selected[0];
  if (!first) return null;

  // 3. 種別が混在していればパネルを出さない。
  //    この型同一性の判定にはロック済みオブジェクトも含める。ロック済みを先に落とすと、たとえば
  //    「ロック済みテキスト＋未ロック付箋」を選択したときに付箋用パネルが出てしまい、
  //    利用者から見える選択（2 種別）と表示（付箋用）が食い違うため。
  if (selected.some((object) => object.type !== first.type)) return null;

  // 4. 選択編集パネルを持たない種別（stamp / comment-thread / image / video / link-card）は対象外。
  const kind = SELECTION_STYLE_KINDS.find((candidate) => candidate === first.type);
  if (!kind) return null;

  // 5. ロック済みを除外する。矢印も線色・線幅を編集できるため図形の対象に含める。
  const targets = selected.filter((object) => object.locked !== true);
  if (targets.length === 0) return null;

  const targetIds = targets.map((object) => object.id);
  return {
    kind,
    targetIds,
    common: resolveCommonValues(kind, targets),
    shapeKindGroup: kind === "shape" ? resolveShapeKindGroup(targets) : null,
  };
}

function resolveShapeKindGroup(targets: readonly CanvasObject[]): "fillable" | "arrow" | "mixed" {
  const shapes = targets.filter((object): object is ShapeCanvasObject => object.type === "shape");
  const hasArrow = shapes.some((shape) => shape.shapeKind === "arrow");
  const hasFillable = shapes.some((shape) => shape.shapeKind !== "arrow");
  if (hasArrow && hasFillable) return "mixed";
  return hasArrow ? "arrow" : "fillable";
}

/**
 * 適用対象すべてで値が一致するフィールドだけを共通値として返す。
 *
 * 呼び出し時点で targets の種別は kind に揃っているが、TypeScript は kind と targets の
 * 対応を追跡できないため、種別ごとに型ガードで絞り込んでからフィールドへアクセスする
 * （全件が通過するので実質的なコストは無い）。
 */
function resolveCommonValues(
  kind: SelectionStyleKind,
  targets: readonly CanvasObject[]
): SelectionStyleCommonValues {
  switch (kind) {
    case "sticky": {
      const stickies = targets.filter(
        (object): object is StickyCanvasObject => object.type === "sticky"
      );
      return {
        ...EMPTY_COMMON,
        color: commonValue(
          stickies.map((object) => normalizeColorForCompare(object.color ?? STICKY_DEFAULT_COLOR))
        ),
        textAlign: commonValue(stickies.map((object) => object.textAlign ?? DEFAULT_TEXT_ALIGN)),
      };
    }
    case "text": {
      const texts = targets.filter((object): object is TextCanvasObject => object.type === "text");
      return {
        ...EMPTY_COMMON,
        color: commonValue(texts.map((object) => normalizeTextColorForCompare(object.color))),
        fill: commonValue(texts.map((object) => normalizeTextFillForCompare(object.fill))),
        fontSize: commonValue(texts.map((object) => object.fontSize ?? DEFAULT_TEXT_FONT_SIZE)),
        bold: commonValue(texts.map((object) => object.bold ?? false)),
        textAlign: commonValue(texts.map((object) => object.textAlign ?? DEFAULT_TEXT_ALIGN)),
      };
    }
    case "table": {
      const tables = targets.filter(
        (object): object is TableCanvasObject => object.type === "table"
      );
      return {
        ...EMPTY_COMMON,
        color: commonValue(tables.map((object) => normalizeTextColorForCompare(object.color))),
        fontSize: commonValue(tables.map((object) => object.fontSize ?? DEFAULT_TEXT_FONT_SIZE)),
      };
    }
    case "section": {
      const sections = targets.filter(
        (object): object is SectionCanvasObject => object.type === "section"
      );
      return {
        ...EMPTY_COMMON,
        fill: commonValue(
          sections.map((object) => normalizeColorForCompare(object.fill ?? SECTION_DEFAULT_FILL))
        ),
      };
    }
    case "shape": {
      const shapes = targets.filter(
        (object): object is ShapeCanvasObject => object.type === "shape"
      );
      return {
        ...EMPTY_COMMON,
        fill: commonValue(shapes.map((object) => normalizeShapeFillForCompare(object.fill))),
        stroke: commonValue(
          shapes.map((object) =>
            normalizeShapeStrokeForCompare(
              object.shapeKind === "arrow"
                ? resolveArrowStrokeColor(object.stroke ?? SHAPE_DEFAULT_STROKE)
                : object.stroke
            )
          )
        ),
        strokeWidth: commonValue(
          shapes.map((object) => resolveShapeStrokeWidthKind(object.strokeWidth))
        ),
        strokePattern: commonValue(
          shapes.map((object) => resolveStrokePattern(object.strokePattern))
        ),
      };
    }
    case "connector": {
      const connectors = targets.filter(
        (object): object is ConnectorCanvasObject => object.type === "connector"
      );
      return {
        ...EMPTY_COMMON,
        stroke: commonValue(
          connectors.map((object) => normalizeConnectorStrokeForCompare(object.stroke))
        ),
        strokeWidth: commonValue(
          connectors.map((object) => resolveConnectorStrokeWidthKind(object.strokeWidth))
        ),
      };
    }
    case "pen-stroke": {
      const strokes = targets.filter(
        (object): object is PenStrokeCanvasObject => object.type === "pen-stroke"
      );
      return {
        ...EMPTY_COMMON,
        color: commonValue(strokes.map((object) => normalizeColorForCompare(object.color))),
        thickness: commonValue(strokes.map((object) => resolvePenThicknessKind(object.thickness))),
        strokePattern: commonValue(
          strokes.map((object) => resolveStrokePattern(object.strokePattern))
        ),
      };
    }
  }
}

/** 全件が同じ値ならその値、1 件でも異なる（または null が混ざる）なら null を返す。 */
function commonValue<T>(values: readonly T[]): T | null {
  const first = values[0];
  if (first === undefined || first === null) return null;
  return values.every((value) => value === first) ? first : null;
}
